import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useOrganization } from "./useOrganization";

export type PlatformRole = 'super_admin' | 'support' | 'member' | 'guest';
export type OrganizationRole = 'admin' | 'member' | 'guest';

interface UserRoles {
  platformRole: PlatformRole;
  organizationRole: OrganizationRole;
  isAdmin: boolean;
  isPlatformAdmin: boolean;
  isOrgAdmin: boolean;
  hasAccessToAllOrgs: boolean;
}

export const useUserRoles = () => {
  const { user } = useAuth();
  const { organization } = useOrganization();

  console.log('useUserRoles - user:', !!user, 'user.id:', user?.id);
  console.log('useUserRoles - organization:', !!organization, 'org.id:', organization?.id);

  const { data: roles, isLoading, error } = useQuery({
    queryKey: ['user-roles', user?.id, organization?.id],
    queryFn: async (): Promise<UserRoles> => {
      if (!user || !organization) {
        return {
          platformRole: 'guest',
          organizationRole: 'guest',
          isAdmin: false,
          isPlatformAdmin: false,
          isOrgAdmin: false,
          hasAccessToAllOrgs: false,
        };
      }

      // First get the user's profile to use the correct ID
      // CRITICAL: Support both old and new profile schema
      // Old schema: profiles.id = auth.users.id (auth_user_id is NULL)
      // New schema: profiles.auth_user_id = auth.users.id
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .or(`id.eq.${user.id},auth_user_id.eq.${user.id}`)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (!profile) {
        return {
          platformRole: 'guest',
          organizationRole: 'guest',
          isAdmin: false,
          isPlatformAdmin: false,
          isOrgAdmin: false,
          hasAccessToAllOrgs: false,
        };
      }

      // Get platform role
      const { data: platformRoleData } = await supabase
        .from('platform_user_roles')
        .select('role')
        .eq('user_id', profile.id)
        .single();

      const platformRole: PlatformRole = platformRoleData?.role || 'member';

      // Get organization role
      const { data: orgRoleData } = await supabase
        .from('organization_user_roles')
        .select('role')
        .eq('user_id', profile.id)
        .eq('org_id', organization.id)
        .maybeSingle();

      const organizationRole: OrganizationRole = orgRoleData?.role || 'guest';

      const isPlatformAdmin = platformRole === 'super_admin' || platformRole === 'support';
      const isOrgAdmin = organizationRole === 'admin';
      const isAdmin = isPlatformAdmin || isOrgAdmin;
      const hasAccessToAllOrgs = platformRole === 'super_admin' || platformRole === 'support';

      return {
        platformRole,
        organizationRole,
        isAdmin,
        isPlatformAdmin,
        isOrgAdmin,
        hasAccessToAllOrgs,
      };
    },
    enabled: !!user && !!organization,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const canQueryRoles = !!user && !!organization;
  const actuallyLoading = !canQueryRoles || isLoading;

  console.log('useUserRoles - canQueryRoles:', canQueryRoles, 'actuallyLoading:', actuallyLoading);
  console.log('useUserRoles - roles result:', roles);

  return {
    roles: roles || {
      platformRole: 'guest' as PlatformRole,
      organizationRole: 'guest' as OrganizationRole,
      isAdmin: false,
      isPlatformAdmin: false,
      isOrgAdmin: false,
      hasAccessToAllOrgs: false,
    },
    isLoading: actuallyLoading,
    error,
  };
};